import React from 'react';
import { ShieldCheck, Wifi, Coins } from 'lucide-react';
import SectionLabel from '../ui-shree/SectionLabel';

/**
 * Luxury2030 — the brand's response to Matter of Form's "Luxury 2030"
 * thesis: the next decade of luxury will not be won by a logo. It will
 * be won by verifiable physical integrity and customer sovereignty.
 *
 * Three editorial pillars connect this thesis to Shreesavaa's stack:
 *  1. Verifiable Physical Integrity  → Atmosic IoT in every vessel
 *  2. Customer Sovereignty           → 211 Million $SEVA ledger, capped
 *  3. Digital Concierges             → Atreya · Savitri · Indra
 *
 * Mounted on Home directly after AgenticStack, so the named agents have
 * a frame the reader can already place them in.
 */

const PILLARS = [
  {
    id:   'witness',
    icon: Wifi,
    overline: 'Pillar I · Verifiable physical integrity',
    title:    'A witness, not a wrapper.',
    body:
      'Old luxury proved itself with packaging. New luxury proves itself with sensors. Every Shreesavaa vessel carries an Atmosic IoT signal that records its own volume, its own dispense cadence, its own refill — continuously, not on a press release.',
    chip: 'Atmosic · always-on witness',
  },
  {
    id:   'sovereignty',
    icon: Coins,
    overline: 'Pillar II · Customer sovereignty',
    title:    'A ledger, not a loyalty programme.',
    body:
      'A logo can be stretched. A token supply cannot. $SEVA is fixed at 211 Million — written once, on chain, never expanded. Each token is signed against a physical event. Your wallet is not a points balance. It is your share of the witnessed economy.',
    chip: '211,000,000 · written once',
  },
  {
    id:   'concierges',
    icon: ShieldCheck,
    overline: 'Pillar III · Digital concierges',
    title:    'A team, named.',
    body:
      'Behind the witness and the ledger sit three named agents — Atreya, Savitri, Indra — the digital concierges of Luxury 2030. Vedic names, written meanings, a single physical job each. No mascot, no chatbot — a discipline.',
    chip: 'Atreya · Savitri · Indra',
  },
];

const Luxury2030 = ({ testId = 'home-luxury-2030' }) => (
  <section
    id="luxury-2030"
    data-testid={testId}
    className="px-6 md:px-12 py-24 md:py-32 bg-[#7C9CB1] text-[#F4F8F9] border-t border-[#90B0BF]"
  >
    <div className="max-w-[1400px] mx-auto">
      {/* Header — reframes the section */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-16 md:mb-20">
        <div className="md:col-span-7">
          <p
            data-testid="luxury-2030-overline"
            className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#F4F8F9]/75 mb-6"
          >
            <span className="inline-block w-7 h-px bg-[#F4F8F9]/75 mr-3 align-middle" />
            Luxury 2030 · The Proof of Purity
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#F4F8F9] font-light leading-[1.05]">
            The new luxury
            <br />
            is not a logo.
            <br />
            <em className="text-[#F4F8F9]/80">It is a witness.</em>
          </h2>
        </div>
        <div className="md:col-span-5 md:pt-4">
          <p className="font-serif italic text-lg md:text-xl text-[#F4F8F9]/90 leading-relaxed max-w-md">
            Matter of Form's <span className="not-italic font-mono text-[12px] tracking-[0.2em] uppercase">Luxury 2030</span> thesis names the shift bluntly:
            the next decade of value will be paid to brands that can <em>prove</em> physical
            integrity and return sovereignty to the guest. Logos no longer carry weight on their own.
          </p>
          <p className="mt-5 font-light text-[#F4F8F9]/85 leading-relaxed max-w-md">
            Shreesavaa is the answer in three pillars — written below, signed in code.
          </p>
        </div>
      </div>

      {/* Three pillars — bordered cards on the steel-blue field */}
      <ol className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
        {PILLARS.map((p, i) => {
          const Icon = p.icon;
          return (
            <li
              key={p.id}
              data-testid={`luxury-2030-pillar-${p.id}`}
              className="relative border border-[#F4F8F9]/30 p-8 md:p-10 bg-[#7C9CB1] flex flex-col"
            >
              {/* Gold rule, top */}
              <span
                aria-hidden
                className="absolute top-0 left-8 right-8 h-px"
                style={{
                  background:
                    'linear-gradient(90deg, transparent, #D4AF5A 35%, #D4AF5A 65%, transparent)',
                }}
              />
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/70 mb-5">
                {p.overline}
              </p>
              <Icon size={26} strokeWidth={1.2} className="text-[#F4F8F9] mb-5" />
              <h3 className="font-serif text-2xl md:text-3xl text-[#F4F8F9] font-light leading-tight">
                {p.title}
              </h3>
              <p className="mt-5 text-[#F4F8F9]/85 font-light leading-relaxed">
                {p.body}
              </p>
              <p
                data-testid={`luxury-2030-pillar-${p.id}-chip`}
                className="mt-6 pt-5 border-t border-[#F4F8F9]/25 font-mono text-[10px] tracking-[0.28em] uppercase text-[#F4F8F9]/85"
              >
                {p.chip}
              </p>
            </li>
          );
        })}
      </ol>

      {/* Quiet signature — closes the loop with the named agents */}
      <p
        data-testid="luxury-2030-signature"
        className="mt-14 font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/75 text-center"
      >
        Witnessed by Atreya · Stewarded by Savitri · Signed by Indra
      </p>
    </div>
  </section>
);

export default Luxury2030;
